import { useCallback, useEffect, useRef, useState } from 'react';
import {
  draftSaveHasWork,
  draftSaveIndicator,
  draftSaveNeedsLeaveGuard,
  type DraftSaveFlushResult,
  type DraftSaveQueue,
  type DraftSaveSnapshot,
} from '@dukanti/shared';
import { useNavBlocker, type NavBlockedNavigation } from '@/hooks/use-nav-blocker';

/**
 * What the leave question says, if it is open:
 * - `saving`: the flush before leaving takes longer than the grace period;
 * - `failed` / `conflict` / `rejected`: the queue cannot save what it holds;
 * - `unsaved`: the queue is saved, but another part of the screen (a region
 *   dialog's invalid field or upload) holds work that would be lost.
 */
export type EditorLeaveState = 'closed' | 'saving' | 'failed' | 'conflict' | 'rejected' | 'unsaved';

export interface EditorLeaveGuard {
  readonly state: EditorLeaveState;
  /** `failed`: resend; `conflict`: reload and save on top. Leaves once saved. */
  readonly retry: () => void;
  /** Leave without saving: drops what the queue still holds, then navigates. */
  readonly leave: () => void;
  /** Cancels the navigation; the merchant keeps editing. */
  readonly stay: () => void;
}

export interface UseEditorLeaveGuardOptions {
  /** How long a flush may take before the "Saving your changes…" question shows. */
  readonly graceMs?: number;
}

/** The leave grace period of PD11: a quick flush never shows a dialog. */
export const EDITOR_LEAVE_GRACE_MS = 500;

type TroubleState = 'failed' | 'conflict' | 'rejected';

function troubleOf(snapshot: DraftSaveSnapshot): TroubleState | null {
  const indicator = draftSaveIndicator(snapshot);
  return indicator === 'failed' || indicator === 'conflict' || indicator === 'rejected'
    ? indicator
    : null;
}

/**
 * Epic #228 P4 (PD11, PRD §11.5): leaving the editor never loses an edit
 * silently and never asks for nothing.
 *
 * - Armed only while the queue has unconfirmed work or a failure
 *   (`draftSaveNeedsLeaveGuard`): saved-but-unpublished leaves freely.
 * - With work only: flush, then leave. No dialog when it settles within the
 *   grace period; otherwise "Saving your changes…" until it does.
 * - Failed, conflict, kept rejection, or a flush that fails: a question with
 *   Retry (or Reload), "Leave without saving" and Stay.
 * - The shell's own dialog stays closed: this scope claims the block
 *   (`useNavBlocker(…, { onBlocked })`). The browser's tab-close prompt
 *   stays while armed (shell provider).
 */
export function useEditorLeaveGuard(
  queue: DraftSaveQueue,
  snapshot: DraftSaveSnapshot,
  { graceMs = EDITOR_LEAVE_GRACE_MS }: UseEditorLeaveGuardOptions = {},
): EditorLeaveGuard {
  const [state, setState] = useState<EditorLeaveState>('closed');
  const navigationRef = useRef<NavBlockedNavigation | null>(null);
  // Every decision bumps the episode, so a flush that settles after Stay or
  // Leave changes nothing.
  const episodeRef = useRef(0);
  const graceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearGrace = useCallback((): void => {
    if (graceRef.current === null) return;
    clearTimeout(graceRef.current);
    graceRef.current = null;
  }, []);

  useEffect(() => clearGrace, [clearGrace]);

  const finish = useCallback((): void => {
    const navigation = navigationRef.current;
    navigationRef.current = null;
    episodeRef.current += 1;
    clearGrace();
    setState('closed');
    navigation?.proceed();
  }, [clearGrace]);

  const settle = useCallback(
    (episode: number, result: DraftSaveFlushResult): void => {
      if (episode !== episodeRef.current || navigationRef.current === null) return;
      clearGrace();
      const current = queue.getSnapshot();
      const trouble = troubleOf(current);
      if (trouble !== null) {
        setState(trouble);
        return;
      }
      if (!result.ok || draftSaveHasWork(current)) {
        setState('failed');
        return;
      }
      if (navigationRef.current.hasOtherDirtyScopes()) {
        setState('unsaved');
        return;
      }
      finish();
    },
    [clearGrace, finish, queue],
  );

  const drain = useCallback(
    (run: () => Promise<DraftSaveFlushResult>, showSavingAt: number): void => {
      const episode = episodeRef.current;
      clearGrace();
      if (showSavingAt <= 0) {
        setState('saving');
      } else {
        graceRef.current = setTimeout(() => {
          graceRef.current = null;
          if (episode === episodeRef.current) setState('saving');
        }, showSavingAt);
      }
      void run().then((result) => settle(episode, result));
    },
    [clearGrace, settle],
  );

  const onBlocked = useCallback(
    (navigation: NavBlockedNavigation): void => {
      episodeRef.current += 1;
      navigationRef.current = navigation;
      const current = queue.getSnapshot();
      const trouble = troubleOf(current);
      if (trouble !== null) {
        setState(trouble);
        return;
      }
      drain(() => queue.flush(), graceMs);
    },
    [drain, graceMs, queue],
  );

  useNavBlocker(draftSaveNeedsLeaveGuard(snapshot), { onBlocked });

  const retry = useCallback((): void => {
    if (navigationRef.current === null) return;
    episodeRef.current += 1;
    drain(() => (state === 'conflict' ? queue.reload() : queue.retry()), 0);
  }, [drain, queue, state]);

  const leave = useCallback((): void => {
    queue.discard();
    finish();
  }, [finish, queue]);

  const stay = useCallback((): void => {
    const navigation = navigationRef.current;
    navigationRef.current = null;
    episodeRef.current += 1;
    clearGrace();
    setState('closed');
    navigation?.reset();
  }, [clearGrace]);

  return { state, retry, leave, stay };
}
